import React from 'react';
import { cn } from 'lib/utils';

export interface StatCardProps {
    title: string;
    value: string | number;
    icon?: React.ReactNode;
    iconColor?: 'total' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple';
    valueColor?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
    suffix?: string;
    className?: string;
}

const iconTone: Record<NonNullable<StatCardProps['iconColor']>, string> = {
    total: 'bg-neutral-100 text-neutral-700',
    primary: 'bg-navy-50 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger: 'bg-destructive/10 text-destructive',
    info: 'bg-info/10 text-info',
    purple: 'bg-navy-50 text-primary',
};

const valueTone: Record<NonNullable<StatCardProps['valueColor']>, string> = {
    default: 'text-foreground',
    primary: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-destructive',
};

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    icon,
    iconColor = 'primary',
    valueColor = 'default',
    suffix = '',
    className = '',
}) => {
    return (
        <div
            className={cn(
                'rounded border border-border bg-card px-4 py-3 transition-colors duration-fast hover:border-neutral-300',
                className
            )}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-0">
                    <h3 className="text-xs font-medium text-muted-foreground truncate">
                        {title}
                    </h3>
                    <p
                        className={cn(
                            'text-xl font-semibold tabular-nums leading-tight tracking-tight',
                            valueTone[valueColor]
                        )}
                    >
                        {value}
                        {suffix && (
                            <span className="text-sm font-normal text-muted-foreground ml-0.5">
                                {suffix}
                            </span>
                        )}
                    </p>
                </div>
                {icon && (
                    <div
                        className={cn(
                            'flex h-9 w-9 items-center justify-center rounded shrink-0',
                            iconTone[iconColor]
                        )}
                    >
                        {icon}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StatCard;
